// Configuração da API Intermediária
// NOTA: Fallback hardcoded para garantir funcionamento em produção
// A segurança real está no backend com JWT e validação de permissões
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sistema-agendamento-backend-fm1o.onrender.com/api';

// Configuração do Supabase (backup para Edge Functions)
// SUPABASE_ANON_KEY é pública e segura - apenas permite operações de leitura
export const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://apnimqlklbywrqetnamf.supabase.co';
export const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Headers padrão
export const getHeaders = (includeAuth = true) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Adicionar header secreto do frontend (verificação de segurança)
  const frontendSecret = process.env.REACT_APP_FRONTEND_SECRET;
  if (!frontendSecret) {
    console.error('⚠️ FRONTEND_SECRET não configurado!');
  }
  headers['X-Frontend-Secret'] = frontendSecret || '';

  if (includeAuth) {
    const token = localStorage.getItem('@sistema-up:token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Função para renovar o token
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// Função genérica para requisições HTTP
export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = getHeaders(true);

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    // Verificar se a resposta é OK
    if (!response.ok) {
      // Se for erro 401 (Unauthorized), tentar renovar o token
      if (response.status === 401 && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/refresh')) {
        const refreshToken = localStorage.getItem('@sistema-up:refreshToken');
        
        if (refreshToken && !isRefreshing) {
          isRefreshing = true;
          
          try {
            const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
              method: 'POST',
              headers: getHeaders(false),
              body: JSON.stringify({ refreshToken }),
            });

            if (refreshResponse.ok) {
              const data = await refreshResponse.json();
              localStorage.setItem('@sistema-up:token', data.token);
              localStorage.setItem('@sistema-up:refreshToken', data.refreshToken || '');
              
              onTokenRefreshed(data.token);
              
              // Repetir a requisição original com o novo token
              const retryResponse = await fetch(url, {
                ...options,
                headers: getHeaders(true),
              });
              
              if (!retryResponse.ok) {
                const errorData = await retryResponse.json().catch(() => ({}));
                const error = new Error(errorData.error || errorData.message || `Erro ${retryResponse.status}`);
                (error as any).response = { data: errorData, status: retryResponse.status };
                throw error;
              }
              
              return await retryResponse.json();
            } else {
              // Refresh token falhou, disparar evento de sessão expirada
              localStorage.removeItem('@sistema-up:token');
              localStorage.removeItem('@sistema-up:refreshToken');
              window.dispatchEvent(new CustomEvent('session-expired'));
              throw new Error('Sessão expirada. Faça login novamente.');
            }
          } catch (error) {
            localStorage.removeItem('@sistema-up:token');
            localStorage.removeItem('@sistema-up:refreshToken');
            window.dispatchEvent(new CustomEvent('session-expired'));
            throw error;
          } finally {
            isRefreshing = false;
          }
        } else if (isRefreshing) {
          // Aguardar o refresh token atual
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((token: string) => {
              const retryResponse = fetch(url, {
                ...options,
                headers: getHeaders(true),
              });
              
              retryResponse.then(async (res) => {
                if (!res.ok) {
                  const errorData = await res.json().catch(() => ({}));
                  const error = new Error(errorData.error || errorData.message || `Erro ${res.status}`);
                  (error as any).response = { data: errorData, status: res.status };
                  reject(error);
                } else {
                  resolve(res.json());
                }
              });
            });
          });
        } else {
          // Não há refresh token, disparar evento de sessão expirada
          localStorage.removeItem('@sistema-up:token');
          window.dispatchEvent(new CustomEvent('session-expired'));
          throw new Error('Sessão expirada. Faça login novamente.');
        }
      }
      
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.error || errorData.message || `Erro ${response.status}`);
      (error as any).response = { data: errorData, status: response.status };
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error('Erro na requisição API:', error);
    throw error;
  }
};

// Métodos HTTP
export const api = {
  get: <T = any>(endpoint: string) => apiRequest<T>(endpoint),
  post: <T = any>(endpoint: string, data: any) => 
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  put: <T = any>(endpoint: string, data: any) => 
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  patch: <T = any>(endpoint: string, data: any) => 
    apiRequest<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: <T = any>(endpoint: string) => 
    apiRequest<T>(endpoint, {
      method: 'DELETE',
    }),
};

export default api;
